import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FolderOpen, FileText, ChevronRight, ArrowLeft, Check, AlertTriangle, Search } from 'lucide-react';
import { API } from '../App';

export default function FileTreeBrowser({ open, onClose, repo, onConfirm }) {
  const [tree, setTree] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [pathHistory, setPathHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const loadTree = useCallback(async (path = '') => {
    if (!repo) return;
    setLoading(true);
    setError('');
    try {
      const [owner, name] = repo.full_name.split('/');
      const r = await API.get(`/api/github/repos/${owner}/${name}/tree?path=${encodeURIComponent(path)}`);
      setTree(r.data.items || []);
      setCurrentPath(path);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load files');
    }
    setLoading(false);
  }, [repo]);

  useEffect(() => {
    if (open && repo) {
      setSelected([]);
      setSearch('');
      setPathHistory([]);
      loadTree('');
    }
  }, [open, repo, loadTree]);

  const navigateInto = (path) => {
    setPathHistory(prev => [...prev, currentPath]);
    loadTree(path);
  };

  const navigateBack = () => {
    const prev = pathHistory[pathHistory.length - 1] || '';
    setPathHistory(h => h.slice(0, -1));
    loadTree(prev);
  };

  const toggleSelect = (item) => {
    const key = item.path;
    if (selected.find(s => s.path === key)) {
      setSelected(prev => prev.filter(s => s.path !== key));
    } else {
      if (item.size > 10240) {
        // Still allow selection but warn
      }
      setSelected(prev => [...prev, { path: item.path, name: item.name, size: item.size }]);
    }
  };

  const isSelected = (path) => selected.some(s => s.path === path);

  const totalSize = selected.reduce((sum, s) => sum + (s.size || 0), 0);
  const overLimit = totalSize > 51200; // 50KB

  const filtered = tree.filter(item =>
    !search || item.name?.toLowerCase().includes(search.toLowerCase())
  );

  // Sort: folders first, then files
  const sorted = [...filtered].sort((a, b) => {
    if (a.type === 'dir' && b.type !== 'dir') return -1;
    if (a.type !== 'dir' && b.type === 'dir') return 1;
    return a.name.localeCompare(b.name);
  });

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-lg bg-[#1A1A1A] border border-[#2A2A2A] max-h-[80vh] flex flex-col"
          onClick={e => e.stopPropagation()}
          data-testid="file-tree-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A]">
            <div className="flex items-center gap-2 min-w-0">
              {(currentPath || pathHistory.length > 0) && (
                <button onClick={navigateBack}
                  className="p-1 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#A0A0A0] hover:text-[#FF4D00]">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <div className="min-w-0">
                <span className="text-xs font-mono text-[#FF4D00] truncate block">{repo?.full_name}</span>
                {currentPath && <span className="text-[10px] font-mono text-[#A0A0A0] truncate block">/{currentPath}</span>}
              </div>
            </div>
            <button onClick={onClose} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#A0A0A0] hover:text-[#F5F5F5]">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Search */}
          <div className="p-3 border-b border-[#2A2A2A]">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Filter files..."
                className="w-full pl-9 pr-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00]"
                data-testid="file-search-input"
              />
            </div>
          </div>

          {/* File tree */}
          <div className="flex-1 overflow-y-auto p-2" style={{ maxHeight: '45vh' }}>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-5 h-5 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="p-4 text-center">
                <p className="text-sm text-[#EF4444]">{error}</p>
                <button onClick={() => loadTree(currentPath)} className="text-xs text-[#FF4D00] mt-2">Retry</button>
              </div>
            ) : sorted.length === 0 ? (
              <p className="text-sm text-[#A0A0A0] text-center py-6">Empty directory</p>
            ) : (
              <div className="space-y-0.5">
                {sorted.map((item, i) => (
                  <div
                    key={item.path || i}
                    className={`flex items-center gap-2 px-3 py-2 min-h-[44px] transition-colors ${
                      item.type !== 'dir' && isSelected(item.path)
                        ? 'bg-[#FF4D00]/10 border border-[#FF4D00]/30'
                        : 'hover:bg-[#2A2A2A]/30 border border-transparent'
                    }`}
                    data-testid={`tree-item-${i}`}
                  >
                    {item.type === 'dir' ? (
                      <button onClick={() => navigateInto(item.path)} className="flex items-center gap-2 flex-1 text-left">
                        <FolderOpen className="w-4 h-4 text-[#FF4D00] shrink-0" />
                        <span className="text-sm truncate">{item.name}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-[#A0A0A0] ml-auto shrink-0" />
                      </button>
                    ) : (
                      <button onClick={() => toggleSelect(item)} className="flex items-center gap-2 flex-1 text-left">
                        <div className={`w-4 h-4 border flex items-center justify-center shrink-0 ${
                          isSelected(item.path) ? 'bg-[#FF4D00] border-[#FF4D00]' : 'border-[#2A2A2A]'
                        }`}>
                          {isSelected(item.path) && <Check className="w-3 h-3 text-black" />}
                        </div>
                        <FileText className="w-3.5 h-3.5 text-[#A0A0A0] shrink-0" />
                        <span className="text-sm truncate">{item.name}</span>
                        <div className="ml-auto flex items-center gap-1.5 shrink-0">
                          {item.size > 10240 && (
                            <AlertTriangle className="w-3 h-3 text-[#F59E0B]" title="File exceeds 10KB — will be truncated" />
                          )}
                          <span className="text-[9px] font-mono text-[#A0A0A0]">
                            {item.size > 1024 ? `${Math.round(item.size / 1024)}KB` : `${item.size || 0}B`}
                          </span>
                        </div>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selection summary + confirm */}
          <div className="p-4 border-t border-[#2A2A2A]">
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3 max-h-20 overflow-y-auto">
                {selected.map(s => (
                  <span key={s.path} className="text-[10px] px-2 py-1 bg-[#0F0F0F] border border-[#FF4D00]/20 text-[#FF4D00] flex items-center gap-1">
                    <FileText className="w-2.5 h-2.5" />
                    {s.name}
                    <button onClick={() => setSelected(prev => prev.filter(p => p.path !== s.path))}
                      className="text-[#A0A0A0] hover:text-[#EF4444] ml-0.5">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-mono text-[#A0A0A0]">
                {selected.length} file{selected.length !== 1 ? 's' : ''} selected
                {totalSize > 0 && ` · ${Math.round(totalSize / 1024)}KB`}
                {overLimit && (
                  <span className="text-[#F59E0B] ml-1">
                    <AlertTriangle className="w-3 h-3 inline" /> Over 50KB limit — will be truncated
                  </span>
                )}
              </div>
              <button
                onClick={() => onConfirm(selected, repo)}
                disabled={selected.length === 0}
                className="px-4 py-2 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-40 flex items-center gap-1.5"
                data-testid="confirm-files-btn"
              >
                <Check className="w-4 h-4" />
                Attach {selected.length > 0 ? `(${selected.length})` : ''}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
